import React, { createContext, useCallback, useEffect, useMemo, useState } from 'react';
import { collection, getDocs, query, where } from 'firebase/firestore';
import { firestore } from '../utils/firebaseConfig.js';

export const DataContext = createContext(null);

const WEEK = 7 * 24 * 60 * 60 * 1000;
const RECENT_WEEKS = 18; // roughly 120 days, with a small weekly-boundary buffer

const formatPeriod = (start) => {
    const end = new Date(start.getTime() + 6 * 24 * 60 * 60 * 1000);
    return `Period: [${start.getMonth() + 1}, ${start.getDate()}, ${start.getFullYear()}] to [${end.getMonth() + 1}, ${end.getDate()}, ${end.getFullYear()}]`;
};

const recentPeriodLabels = () => {
    const today = new Date();
    const currentSunday = new Date(today.getFullYear(), today.getMonth(), today.getDate() - today.getDay());
    return Array.from({ length: RECENT_WEEKS }, (_, index) => formatPeriod(new Date(currentSunday.getTime() - index * WEEK)));
};

const snapshotRows = (snapshot) => snapshot.docs.map((document) => ({ id: document.id, ...document.data() }));

export const DataProvider = ({ children }) => {
    const [employeeKpis, setEmployeeKpis] = useState([]);
    const [loading, setLoading] = useState(true);
    const [fetchingHistorical, setFetchingHistorical] = useState(false);
    const [historicalLoaded, setHistoricalLoaded] = useState(false);
    const [error, setError] = useState('');

    const refresh = useCallback(async () => {
        setLoading(true);
        setError('');

        try {
            const recentQuery = query(
                collection(firestore, 'employee_kpis'),
                where('week_summary', 'in', recentPeriodLabels())
            );
            const employeeSnapshot = await getDocs(recentQuery);
            setEmployeeKpis(snapshotRows(employeeSnapshot));
            setHistoricalLoaded(false);
        } catch (fetchError) {
            console.error('Error fetching recent KPI data:', fetchError);
            setError('Unable to load KPI data. Check your connection and try again.');
        } finally {
            setLoading(false);
        }
    }, []);

    const fetchHistorical = useCallback(async () => {
        if (historicalLoaded || fetchingHistorical) return;
        setFetchingHistorical(true);
        setError('');

        try {
            const employeeSnapshot = await getDocs(collection(firestore, 'employee_kpis'));
            setEmployeeKpis(snapshotRows(employeeSnapshot));
            setHistoricalLoaded(true);
        } catch (fetchError) {
            console.error('Error fetching historical KPI data:', fetchError);
            setError('Unable to fetch historical data. Please try again.');
            throw fetchError;
        } finally {
            setFetchingHistorical(false);
        }
    }, [fetchingHistorical, historicalLoaded]);

    useEffect(() => {
        refresh();
    }, [refresh]);

    const value = useMemo(() => ({
        employeeKpis,
        loadingEmployee: loading,
        fetchingHistorical,
        historicalLoaded,
        error,
        refresh,
        fetchHistorical,
    }), [employeeKpis, loading, fetchingHistorical, historicalLoaded, error, refresh, fetchHistorical]);

    if (loading) return <div className="app-status" role="status">Loading recent KPI data…</div>;

    if (error && !employeeKpis.length) {
        return (
            <div className="app-status" role="alert">
                <p>{error}</p>
                <button type="button" onClick={refresh}>Retry</button>
            </div>
        );
    }

    return <DataContext.Provider value={value}>{children}</DataContext.Provider>;
};
